import {
    Component,
    EventEmitter,
    forwardRef,
    Input,
    Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { take } from 'rxjs/operators';

import { ApiError, AuthService } from '../../../../core';
import { RegiaoFilter } from '../../../../home/regiao/filter/regiao-filter';
import { RegiaoCrudHttpService } from '../../../../home/regiao/services';
import { regiaoSituacaoAtiva } from '../../../../home/regiao/situacao';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { IOption } from '../multi-select/models/option.interface';
import { ToastService } from '../toast/services/toast.service';

@Component({
  selector: 'sgt-novo-regiao-multiselect',
  templateUrl: './novo-regiao-multiselect.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NovoRegiaoMultiselectComponent),
      multi: true
    }
  ]
})
export class NovoRegiaoMultiselectComponent
  extends CustomControlValueAccessor<number[]> {
  @Input() idsRegioes: number[];
  @Input() variante: 'filtro' | undefined;

  @Input() showNullOption = false;
  @Input() nullOption = false;
  @Output() nullOptionChange = new EventEmitter<boolean>();

  @Input() somenteAtivas = true;

  @Output() idsRegioesChange = new EventEmitter<number[]>();

  private readonly _defaultSettings: IMultiSelectSettings = {
    placeholder: 'Nenhuma Região',
    singular: 'Região',
    plural: 'Regiões',
    searchEmptyResultMessage: 'Nenhuma região encontrada',
    showFooter: false
  };
  private _settings: IMultiSelectSettings = { ...this._defaultSettings };

  @Input() set settings(value: Partial<IMultiSelectSettings>) {
    this._settings = { ...this._defaultSettings, ...(value ?? {}) };
  }
  get settings(): IMultiSelectSettings {
    return this._settings;
  }

  options: IOption[] = [];
  private _isLoading = false;

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private regiaoCrudHttpService: RegiaoCrudHttpService
  ) {
    super();
  }

  firstOpened(isFirstOpened: boolean): void {
    if (isFirstOpened && this.options.length === 0 && !this._isLoading) {
      this.load();
    }
  }

  private load(): void {
    this._isLoading = true;
    this.options = [];

    const filter = new RegiaoFilter();
    if (this.somenteAtivas) {
      filter.situacoes = [regiaoSituacaoAtiva.id];
    }
    if (this.authService.isRepresentante()) {
      filter.idRepresentante = this.authService.getIdRepresentante();
    }

    this.regiaoCrudHttpService
      .searchWithFilter(filter, 0, null, null, 3, 'asc')
      .pipe(take(1))
      .subscribe(
        (response) => {
          const regioes = response?.data ?? [];
          const currentIds = this.value ?? [];

          const opts: IOption[] = [];

          if (this.showNullOption) {
            opts.push({
              id: 0,
              label: 'SEM REGIÃO',
              isChecked: currentIds.includes(0)
            });
          }

          regioes.forEach((regiao) => {
            opts.push({
              id: regiao.id,
              label: regiao.nome,
              isChecked: currentIds.includes(regiao.id)
            });
          });

          this.options = opts;
          this._isLoading = false;
        },
        (error: ApiError) => {
          this._isLoading = false;
          this.toastService.error(error.message, { duration: 5000 });
        }
      );
  }

  onSelectionChange(): void {
    this.nullOption = (this.idsRegioes ?? []).includes(0);
    this.nullOptionChange.emit(this.nullOption);
    this.emitChange(this.idsRegioes);
    this.idsRegioesChange.emit(this.idsRegioes);
  }

  onSearch(): void {
    this.idsRegioesChange.emit(this.idsRegioes);
  }

  protected updateValue(newValue: number[] | null): void {
    this.idsRegioes = newValue ?? [];

    if (this.idsRegioes.length > 0 && this.options.length === 0 && !this._isLoading) {
      this.load();
      return;
    }

    this.options.forEach((option) => {
      option.isChecked = this.idsRegioes.includes(option.id);
    });
  }
}
